import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo from './Seo';

const industries = [
  { name: 'Insurance', route: '/industries/insurance', desc: 'Modernizing policy management, claims operations, and compliance workflows through intelligent technology services.' },
  { name: 'Energy & Utilities', route: '/industries/energy', desc: 'Improving service reliability, grid operations, and digital utility workflows with analytics-enabled platforms.' },
  { name: 'Banking & Finance', route: '/industries/banking', desc: 'Strengthening financial operations with secure digital channels, risk governance, and data-driven reporting.' },
  { name: 'Retail', route: '/industries/retail', desc: 'Enhancing omnichannel experiences, supply visibility, and performance decisioning for modern retail enterprises.' },
  { name: 'Healthcare', route: '/industries/healthcare', desc: 'Supporting patient-centric delivery through integration, automation, and trusted healthcare data operations.' },
  { name: 'Telecommunications', route: '/industries/telecom', desc: 'Accelerating telecom transformation with scalable service platforms and high-performance support operations.' },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const IndustriesOverview = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-4 pb-12">
      <Seo
        title="Industries We Serve"
        description="Libsys delivers industry-specific IT services and strategic staffing across insurance, energy & utilities, banking & finance, retail, healthcare, and telecommunications."
        path="/industries"
      />
      <section className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={stagger}
          className="text-center mx-auto mb-16"
        >
          <motion.span variants={fadeUp} className="text-red-tagline block mb-1">
            Industries We Serve
          </motion.span>
          <motion.h1 variants={fadeUp} className="text-h2 text-primary tracking-tight mb-1">
            Deep Domain Expertise Across Key Sectors
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-600 max-w-2xl mx-auto font-medium leading-relaxed text-small">
            We bring specialized knowledge and proven methodologies to each industry we serve — enabling our clients to stay ahead of sector-specific challenges and capitalize on emerging opportunities.
          </motion.p>
          <motion.div variants={fadeUp} className="pt-6 flex justify-center">
            <img src="/industry.png" alt="Industries We Serve" className="w-full max-w-5xl h-auto rounded-3xl shadow-2xl border border-slate-200" />
          </motion.div>
        </motion.div>
 
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {industries.map((ind, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              className="relative overflow-hidden border border-[#236086]/20 bg-[#2e79a8] p-5 lg:p-6 hover:shadow-2xl transition-all rounded-2xl flex flex-col group shadow-lg"
            >
              <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at_18%_18%,white_0,transparent_44%)] pointer-events-none" />
              
              <h3 className="relative z-10 text-[20px] font-black text-white leading-tight mb-2 tracking-tight">
                {ind.name}
              </h3>
              <p className="relative z-10 text-blue-50/90 text-subtext leading-relaxed font-medium mb-4">
                {ind.desc}
              </p>
              
              <Link
                to={ind.route}
                className="relative z-10 mt-auto inline-flex items-center gap-2 text-white font-black text-[10.5px] uppercase tracking-wider hover:gap-3 transition-all active:scale-95 bg-white/10 w-fit px-5 py-2.5 rounded-xl hover:bg-white/20"
              >
                Explore Industry <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default IndustriesOverview;
